import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import BrandLogo from "@components/BrandLogo";
import ActivityPoints from "@components/ActivityPoints";
import DOMPurify from "dompurify";

const isNumericContent = (value) => /^\d+$/.test(String(value ?? "").trim());

const getQuestionLayout = (questionData) => {
  const answerOptions = questionData?.answers ?? [];
  const correctAnswer = questionData?.correctAnswer ?? [];
  const answersAreNumbers =
    answerOptions.length > 0 &&
    answerOptions.every((answer) => isNumericContent(answer.content));

  if (answersAreNumbers) {
    return {
      rows: correctAnswer.map((step, index) => ({
        key: step.question_ident ?? `q${index}`,
        content: step.stepContent,
        img: step.img,
        correctAnswerIdent: step.answer_ident,
        questionIdent: step.question_ident ?? `q${index}`,
      })),
      numberOptions: answerOptions,
    };
  }

  const numberOptions = answerOptions.map((_, index) => ({
    ident: String(index + 1),
    content: String(index + 1),
  }));

  return {
    rows: answerOptions.map((answer, index) => {
      const step = correctAnswer.find(
        (item) => item.answer_ident === answer.ident,
      );

      return {
        key: answer.ident ?? `q${index}`,
        content: answer.content,
        img: answer.img,
        correctAnswerIdent: String(step?.stepContent ?? ""),
        questionIdent: step?.question_ident ?? `q${index}`,
      };
    }),
    numberOptions,
  };
};

export default function SetOrder({
  currentQuestion,
  setCurrentQuestion,
  setAnswers,
  answers,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);
  const currentQuestionData = gameQuestionsQuery?.content?.[currentQuestion];
  const activationDescribe = queryClient.getQueryData([
    "activation-describe",
    ident,
  ]);
  const XGA = width >= 1020;
  const [allAnswers, setAllAnswers] = useState([]);
  const [selectionOrder, setSelectionOrder] = useState([]);
  const isUltraInsider =
    location.pathname.includes("/ultra-insider") ||
    location.pathname.includes("/ultrainsider");

  const { rows, numberOptions } = useMemo(
    () => getQuestionLayout(currentQuestionData),
    [currentQuestionData],
  );

  const buildAnswersFromOrder = (order) =>
    rows.map((row, index) => {
      const assignedPosition = order.indexOf(index);

      return {
        question_ident: row.questionIdent,
        answer_ident:
          assignedPosition === -1
            ? -1
            : numberOptions[assignedPosition]?.ident,
      };
    });

  useEffect(() => {
    if (!currentQuestionData) return;

    const savedCurrentAnswer = answers.find(
      (answer) => answer.question_ident === currentQuestionData.ident,
    );

    if (savedCurrentAnswer?.answer_ident) {
      const savedIdentsByStep = Array.isArray(savedCurrentAnswer.answer_ident)
        ? savedCurrentAnswer.answer_ident
        : String(savedCurrentAnswer.answer_ident).split(",");
      const restoredOrder = [];

      numberOptions.forEach(({ ident: answerIdent }) => {
        const stepIndex = savedIdentsByStep.findIndex(
          (identValue) => identValue === answerIdent,
        );

        if (stepIndex !== -1) {
          restoredOrder.push(stepIndex);
        }
      });

      setSelectionOrder(restoredOrder);
      setAllAnswers(buildAnswersFromOrder(restoredOrder));
      return;
    }

    setSelectionOrder([]);
    setAllAnswers(buildAnswersFromOrder([]));
  }, [currentQuestionData, currentQuestion]);

  const isAnyAnswerMissing = allAnswers.some(
    (answer) => answer.answer_ident === -1,
  );

  const isOrderCorrect = useMemo(() => {
    if (allAnswers.length === 0 || isAnyAnswerMissing) return false;

    return allAnswers.every(
      (answer, index) => answer.answer_ident === rows[index]?.correctAnswerIdent,
    );
  }, [allAnswers, isAnyAnswerMissing, rows]);

  const handleNextButtonClick = () => {
    if (allAnswers.length === 0 || isAnyAnswerMissing) return;

    const currentQuestionIdent = currentQuestionData.ident;
    const answerValue = allAnswers.map((answer) => answer.answer_ident);

    setAnswers((prevAnswers) => {
      const existingAnswerIndex = prevAnswers.findIndex(
        (answer) => answer.question_ident === currentQuestionIdent,
      );

      if (existingAnswerIndex !== -1) {
        const updatedAnswers = [...prevAnswers];
        updatedAnswers[existingAnswerIndex] = {
          question_ident: currentQuestionIdent,
          answer_ident: [...answerValue],
        };
        return updatedAnswers;
      }

      return [
        ...prevAnswers,
        {
          question_ident: currentQuestionIdent,
          answer_ident: [...answerValue],
        },
      ];
    });
    setCurrentQuestion(currentQuestion + 1);
    setSelectionOrder([]);
  };

  const handleStepClick = (index) => {
    setSelectionOrder((prevOrder) => {
      let nextOrder = [...prevOrder];
      const alreadyAssigned = nextOrder.includes(index);

      if (alreadyAssigned) {
        nextOrder = nextOrder.filter((item) => item !== index);
      } else if (nextOrder.length < rows.length) {
        nextOrder.push(index);
      } else {
        return prevOrder;
      }

      setAllAnswers(buildAnswersFromOrder(nextOrder));
      return nextOrder;
    });
  };

  const getStepNumber = (index) => {
    const assignedPosition = selectionOrder.indexOf(index);
    if (assignedPosition === -1) return "";

    const assignedIdent = numberOptions[assignedPosition]?.ident;
    return numberOptions.find((answer) => answer.ident === assignedIdent)
      ?.content;
  };

  const sideImage =
    currentQuestionData?.desktopImgHid && !isOrderCorrect
      ? currentQuestionData[width < 1020 ? "mobileImgHid" : "desktopImgHid"]
      : currentQuestionData?.[width < 1020 ? "mobileImg" : "desktopImg"] ||
        gameQuestionsQuery?.[width < 1020 ? "mobileImg" : "desktopImg"];

  const questionCopy =
    currentQuestionData?.question || currentQuestionData?.title;
  const nextButtonCopy =
    currentQuestionData?.nextButtonCopy ??
    currentQuestionData?.customButtonCopy ??
    "Nächste";
  const progressValue = rows.length
    ? selectionOrder.length / rows.length
    : 0;
  const payout =
    activationDescribe?.payout ??
    currentQuestionData?.points ??
    gameQuestionsQuery?.points;

  if (!currentQuestionData) return null;

  return (
    <section
      className={`game-page set-order-mechanics ${ident} ${
        isUltraInsider ? "ultra-insider" : ""
      }`}
    >
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>
        <div className="main-game-wrppaer">
          <div className="desktop-wrapper">
            <LinearGradientProgressBar value={progressValue} />
            {questionCopy && (
              <div className="game-question">
                <h2
                  className="question"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(questionCopy),
                  }}
                />
              </div>
            )}
            <div className="answer-content">
              <div className="quiz-dropdowns">
                {rows.map((row, index) => {
                  const assignedNumber = getStepNumber(index);

                  return (
                    <button
                      key={row.key}
                      type="button"
                      className={`step-row ${
                        allAnswers[index]?.answer_ident !== -1 ? "selected" : ""
                      }`}
                      onClick={() => handleStepClick(index)}
                    >
                      {currentQuestionData.imgAnswers && row.img ? (
                        <img src={row.img} alt="" className="step-img" />
                      ) : null}
                      <p
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(row.content),
                        }}
                      ></p>
                      <span className="step-number">{assignedNumber}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {currentQuestionData?.questDis && (
              <a
                href={currentQuestionData?.questDisLink}
                target="_blank"
                rel="noopener noreferrer"
                className="answer-dis"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(currentQuestionData?.questDis),
                }}
              ></a>
            )}
          </div>
          <div className="side-img">
            {XGA && <BrandLogo brands={gameQuestionsQuery?.brands} />}
            {sideImage && (
              <div className="img-wrapper">
                <img src={sideImage} alt="" />
                {!!payout && (
                  <ActivityPoints
                    className={isUltraInsider ? "ultra-insider" : ""}
                  >
                    {payout}
                  </ActivityPoints>
                )}
              </div>
            )}
            {XGA && (
              <StickyContent>
                <div className="desktop-buttons">
                  <Button
                    disabled={!isOrderCorrect}
                    action={handleNextButtonClick}
                  >
                    {nextButtonCopy}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
        </div>
      </div>
      {!XGA && (
        <StickyButton
          disabled={!isOrderCorrect}
          action={handleNextButtonClick}
        >
          {nextButtonCopy}
        </StickyButton>
      )}
    </section>
  );
}
